import React, { useEffect, useRef } from 'react';
import { useNotificationModal } from '../contexts/NotificationModalContext';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useEscapeKey } from '../hooks/useEscapeKey';
import '../styles/NotificationModal.css';

function NotificationModal() {
  const { modalState, closeModal, dismissToast, pauseToast, resumeToast } = useNotificationModal();
  const { isOpen, type, title, message, confirmText, cancelText, onConfirm, onCancel, variant } = modalState;

  // Focus trap for keyboard navigation
  const focusTrapRef = useFocusTrap(isOpen);
  const confirmButtonRef = useRef(null);

  // Handle escape key
  useEscapeKey(isOpen, closeModal);

  // Handle body scroll lock and focus management (only for modals, not toasts)
  useEffect(() => {
    if (isOpen && type !== 'toast') {
      document.body.style.overflow = 'hidden';

      // Focus the confirm button (or cancel if it exists) when modal opens
      requestAnimationFrame(() => {
        if (confirmButtonRef.current) {
          confirmButtonRef.current.focus();
        }
      });
    }

    return () => {
      if (type !== 'toast') {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, type]);

  if (!isOpen) return null;

  // Render toast variant
  if (type === 'toast') {
    return (
      <div
        className={`toast toast-${variant}`}
        onMouseEnter={pauseToast}
        onMouseLeave={resumeToast}
        role="status"
        aria-live="polite"
      >
        <span className="toast-icon" aria-hidden="true">
          {variant === 'success' && (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          )}
          {variant === 'error' && (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          )}
          {variant === 'warning' && (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          )}
          {variant === 'default' && (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
          )}
        </span>
        <span className="toast-message">{message}</span>
        <button
          className="toast-close"
          onClick={dismissToast}
          aria-label="Dismiss"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    );
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const getIconForVariant = () => {
    switch (variant) {
      case 'error':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        );
      case 'warning':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        );
      case 'success':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        );
      default:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        );
    }
  };

  return (
    <div
      className="notification-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "notification-modal-title" : undefined}
      aria-describedby="notification-modal-message"
    >
      <div
        ref={focusTrapRef}
        className={`notification-modal-content notification-modal-${variant}`}
      >
        <div className={`notification-modal-icon notification-modal-icon-${variant}`} aria-hidden="true">
          {getIconForVariant()}
        </div>

        {title && <h3 id="notification-modal-title" className="notification-modal-title">{title}</h3>}

        <p id="notification-modal-message" className="notification-modal-message">{message}</p>

        <div className="notification-modal-actions">
          {type === 'confirm' && (
            <button
              className="notification-modal-btn notification-modal-btn-cancel"
              onClick={onCancel}
              aria-label={cancelText}
            >
              {cancelText}
            </button>
          )}
          <button
            ref={confirmButtonRef}
            className={`notification-modal-btn notification-modal-btn-confirm notification-modal-btn-${variant}`}
            onClick={onConfirm}
            aria-label={confirmText}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NotificationModal;
